import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import io from 'socket.io-client';

const SpeechToTextDashboard = () => {
    const [isListening, setIsListening] = useState(false);
    const [transcription, setTranscription] = useState('');
    const [selectedAgent, setSelectedAgent] = useState('AIAgent');
    const [agentResponse, setAgentResponse] = useState('');
    const [socket, setSocket] = useState(null);
    const [agentStatus, setAgentStatus] = useState('idle');
    const [responseHistory, setResponseHistory] = useState([]);
    const [ttsEnabled, setTtsEnabled] = useState(false);
    const [autoSendEnabled, setAutoSendEnabled] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const audioRef = useRef(null);

    useEffect(() => {
        // const newSocket = io('http://localhost:5010');
        const newSocket = io(process.env.REACT_APP_AUDIO_BASE_URL);
        setSocket(newSocket);

        newSocket.on('connect', () => {
            console.log('Connected to Speech-to-Text service');
        });

        newSocket.on('transcription', (message) => {
            setTranscription(message);
        });

        newSocket.on('listeningStatus', (status) => {
            console.log('Listening status:', status);
            setIsListening(status);
        });

        return () => newSocket.disconnect();
    }, []);

    useEffect(() => {
        if (ttsEnabled && agentResponse) {
            playTTS(agentResponse);
        }
    }, [agentResponse, ttsEnabled]);

    useEffect(() => {
        if (autoSendEnabled && transcription && !isListening) {
            console.log('Auto-sending transcription to agent');
            sendToAgent();
        }
    }, [transcription, autoSendEnabled, isListening]);

    const startListening = () => {
        if (socket) {
            setTranscription('');
            socket.emit('startRecognition');
        } else {
            console.error('Socket not connected');
        }
    };

    const stopListening = () => {
        if (socket) {
            socket.emit('stopRecognition');
        } else {
            console.error('Socket not connected');
        }
    };

    const sendToAgent = async () => {
        try {
            setAgentStatus('received request');
            let response;
            if (selectedAgent === 'AIAgent') {
                setAgentStatus('thinking');
                // response = await axios.post('http://localhost:4030/api/reason', { input: transcription });
                response = await axios.post(process.env.REACT_APP_AGENT_EXTENDED_NEURO_URL + '/reason', { input: transcription });
            } else {
                setAgentStatus('thinking');
                // response = await axios.post('http://localhost:4080/query', { query: transcription });
                response = await axios.post(process.env.REACT_APP_LLAMAINDEX_EXTENDED_NEURO_URL + '/query', { query: transcription });
            }
            setAgentStatus('responding');
            const result = response.data.result || response.data.answer;
            setAgentResponse(result);
            setResponseHistory(prevHistory => [...prevHistory, { agent: selectedAgent, query: transcription, response: result }]);
            setAgentStatus('done');
        } catch (error) {
            console.error('Error sending to agent:', error);
            setAgentResponse('Error: ' + error.message);
            setAgentStatus('error');
        }
    };

    const clearResponseHistory = () => {
        setResponseHistory([]);
        setTranscription('');
        setAgentResponse('');
        setAgentStatus('idle');
        if (isListening) {
            stopListening();
        }
    };

    const playTTS = async (text) => {
        try {
            console.log('Sending TTS request for text:', text);
            // const response = await axios.post('http://localhost:5010/api/tts', 
            const response = await axios.post(process.env.REACT_APP_AUDIO_EXTENDED_NEURO_URL + '/tts', 
                { text }, 
                { 
                    responseType: 'blob',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );
            console.log('Received TTS response');
            
            const audioBlob = new Blob([response.data], { type: 'audio/mp3' });
            const audioUrl = URL.createObjectURL(audioBlob);
            
            if (audioRef.current) {
                audioRef.current.src = audioUrl;
                console.log('Playing audio...');
                setIsPlaying(true);
                
                audioRef.current.onended = () => {
                    setIsPlaying(false);
                };
                
                const playPromise = audioRef.current.play();
                
                if (playPromise !== undefined) {
                    playPromise.catch(error => {
                        console.error('Error playing audio:', error);
                        setIsPlaying(false);
                    });
                }
            }
        } catch (error) {
            console.error('Error in TTS playback:', error);
            setIsPlaying(false);
        }
    };

    const toggleTTS = () => {
        setTtsEnabled(!ttsEnabled);
    };

    const toggleAutoSend = () => {
        setAutoSendEnabled(!autoSendEnabled);
    };

    const stopTTS = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            setIsPlaying(false);
        }
    };

    return (
        <div className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4">
            <h2 className="text-2xl font-bold mb-4">Speech-to-Text Dashboard</h2>
            
            <div className="mb-4">
                <button
                    className={`bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded mr-2 ${isListening ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onClick={startListening}
                    disabled={isListening}
                >
                    Start Listening
                </button>
                <button
                    className={`bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded ${!isListening ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onClick={stopListening}
                    disabled={!isListening}
                >
                    Stop Listening
                </button>
                <span className={`ml-4 ${isListening ? 'text-green-500' : 'text-red-500'}`}>
                    {isListening ? 'Listening...' : 'Not Listening'}
                </span>
            </div>

            <div className="mb-4">
                <h3 className="text-lg font-semibold mb-2">Transcription:</h3>
                <div className="border p-2 min-h-[100px]">{transcription}</div>
            </div>

            <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="agentSelect">
                    Select Agent:
                </label>
                <select
                    id="agentSelect"
                    className="shadow border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    value={selectedAgent}
                    onChange={(e) => setSelectedAgent(e.target.value)}
                >
                    <option value="AIAgent">AI Agent</option>
                    <option value="LlamaIndex">LlamaIndex Agent</option>
                </select>
            </div>

            <div className="mb-4 flex items-center">
                <button
                    className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded mr-4"
                    onClick={sendToAgent}
                >
                    Send to Agent
                </button>
                
                <span className={`text-sm font-semibold ${
                    agentStatus === 'idle' ? 'text-gray-500' :
                    agentStatus === 'received request' ? 'text-blue-500' :
                    agentStatus === 'thinking' ? 'text-yellow-500' :
                    agentStatus === 'responding' ? 'text-purple-500' :
                    agentStatus === 'done' ? 'text-green-500' :
                    'text-red-500'
                }`}>
                    {agentStatus.charAt(0).toUpperCase() + agentStatus.slice(1)}
                </span>
            </div>
            
            <div className="mb-4 flex items-center">
                <button
                    className={`bg-purple-500 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded mr-4 ${autoSendEnabled ? 'opacity-50' : ''}`}
                    onClick={toggleAutoSend}
                >
                    {autoSendEnabled ? 'Disable Auto-Send-to-Ai' : 'Enable Auto-Send-to-Ai'}
                </button>
                <span className={`text-sm font-semibold ${autoSendEnabled ? 'text-green-500' : 'text-red-500'}`}>
                    Auto-Send is {autoSendEnabled ? 'enabled' : 'disabled'}
                </span>
            </div>

            <div className="mb-4">
                <h3 className="text-lg font-semibold mb-2">Current Agent Response:</h3>
                <div className="border p-2 min-h-[100px]">{agentResponse}</div>
            </div>

            <div className="mb-4">
                <h3 className="text-lg font-semibold mb-2">Response History:</h3>
                <div className="border p-2 h-[300px] overflow-y-auto">
                    {responseHistory.map((item, index) => (
                        <div key={index} className="mb-4 pb-4 border-b">
                            <p><strong>Agent:</strong> {item.agent}</p>
                            <p><strong>Query:</strong> {item.query}</p>
                            <p><strong>Response:</strong> {item.response}</p>
                        </div>
                    ))}
                </div>
            </div>

            <div className="mb-4 flex items-center">
                <button
                    className={`bg-purple-500 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded mr-4 ${ttsEnabled ? 'opacity-50' : ''}`}
                    onClick={toggleTTS}
                >
                    {ttsEnabled ? 'Disable TTS' : 'Enable TTS'}
                </button>
                <button
                    className={`bg-orange-500 hover:bg-orange-700 text-white font-bold py-2 px-4 rounded mr-4 ${!isPlaying ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onClick={stopTTS}
                    disabled={!isPlaying}
                >
                    Stop TTS Playback
                </button>
                <span className={`text-sm font-semibold ${ttsEnabled ? 'text-green-500' : 'text-red-500'}`}>
                    TTS is {ttsEnabled ? 'enabled' : 'disabled'}
                </span>
            </div>

            

            <button
                className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
                onClick={clearResponseHistory}
            >
                Clear Response History
            </button>

            <audio ref={audioRef} className="hidden" />
        </div>
    );
};

export default SpeechToTextDashboard;
